import * as THREE from "three";
import type { AssetMap } from "../assets";
import {
  AIR_PASS_HEIGHT,
  BANK,
  COURSE_WIDTH,
  CRUISE_SPEED,
  MAX_SPEED,
  MIN_SPEED,
  PLAYER_DRAW,
  TRACK_LENGTH,
} from "../constants";
import { getObject } from "../objects";
import type { GameSim } from "../sim";
import { computeView, toScreenX, toScreenY, type ViewCamera } from "../viewCamera";
import {
  createGovernor,
  isFullResolution,
  isSoftwareRenderer,
  scaleForBudget,
  startLevelFor,
  TOP_LEVEL,
} from "./quality";
import { SHADOW_DIR, SUN_DIR, SUN_SCREEN } from "./sun";
import {
  COMPOSITE_FRAG,
  COMPOSITE_VERT,
  SNOW_FRAG,
  SNOWFALL_FRAG,
  SNOWFALL_VERT,
  SNOW_VERT,
  SPRAY_FRAG,
  SPRAY_VERT,
  SPRITE_FRAG,
  SPRITE_VERT,
} from "./shaders";

export type GameView = {
  render: (sim: GameSim, alpha: number) => void;
  dispose: () => void;
};

const SPRITE_KEYS = [
  "tree-pine",
  "tree-hemlock",
  "rock-0",
  "rock-1",
  "rock-2",
  "rock-3",
  "jump",
  "food-hotdog",
  "food-burger",
] as const;

const MAX_PER: Record<(typeof SPRITE_KEYS)[number], number> = {
  "tree-pine": 360,
  "tree-hemlock": 360,
  "rock-0": 80,
  "rock-1": 80,
  "rock-2": 80,
  "rock-3": 80,
  jump: 40,
  "food-hotdog": 24,
  "food-burger": 12,
};

const MAX_SHADOWS = 420;
const MAX_TRACKS = 160;
const MAX_SPRAY = 220;
const SNOW_COUNT = 1800;

const dummy = new THREE.Object3D();
const COLOR = new THREE.Color();
const WHITE = new THREE.Color(1, 1, 1);

function makeCircleTex(size = 64): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d")!;
  const grd = g.createRadialGradient(size / 2, size / 2, size * 0.12, size / 2, size / 2, size / 2);
  grd.addColorStop(0, "rgba(12,18,16,0.55)");
  grd.addColorStop(0.45, "rgba(12,18,16,0.18)");
  grd.addColorStop(1, "rgba(12,18,16,0)");
  g.fillStyle = grd;
  g.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

function makeFallback(color: string, w = 64, h = 64): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const g = c.getContext("2d")!;
  g.fillStyle = color;
  g.beginPath();
  g.ellipse(w / 2, h / 2, w * 0.38, h * 0.38, 0, 0, Math.PI * 2);
  g.fill();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

// Every sprite is minified: the sources are 192-256px and a tree lands around
// 60 device px on a phone, the rider around 32. Sampling a full-resolution
// texture at 4-6x minification with one bilinear tap is what made distant
// scenery shimmer as the hill scrolled, and it thrashes the texture cache
// doing it. three requests WebGL2 only, so non-power-of-two sources mipmap
// fine -- eight of these are 192x192.
function texFromImage(img: HTMLImageElement | undefined, fallback: THREE.Texture): THREE.Texture {
  if (!img || !img.complete || img.naturalWidth <= 0) return fallback;
  const t = new THREE.Texture(img);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = 4;
  t.needsUpdate = true;
  return t;
}

function makeFinishTex(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 96;
  const g = c.getContext("2d")!;
  g.fillStyle = "#f4f1ea";
  g.fillRect(0, 0, c.width, c.height);
  g.fillStyle = "#1c2e24";
  g.font = "600 56px Fraunces, Times New Roman, serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText("FINISH", c.width / 2, c.height / 2 + 2);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

function speed01(speed: number): number {
  return Math.max(0, Math.min(1, (speed - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)));
}

export function createGlView(canvas: HTMLCanvasElement, assets: AssetMap): GameView {
  THREE.ColorManagement.enabled = false;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    // The scene renders into `rt`, which has no `samples`, and the only
    // geometry ever drawn into the default framebuffer is one fully-covering
    // composite quad -- a quad with no interior edges cannot produce a
    // multisampled edge, so every sample in every pixel resolved to the same
    // value. This was paying for 4x framebuffer memory, tile-RAM pressure and
    // a mandatory resolve to produce a bit-identical image.
    antialias: false,
    alpha: false,
    powerPreference: "high-performance",
    stencil: false,
    depth: true,
    premultipliedAlpha: false,
  });
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.setClearColor(0x0e1a16, 1);

  // A touch device is assumed to be the weaker one until it proves otherwise.
  // The governor lives here rather than in the sim: it must never be able to
  // influence gameplay, and the sim is already render-independent.
  const coarsePointer =
    typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;
  // The unmasked renderer string is the one static signal worth reading, and
  // only for one question: is anything but a CPU drawing this? Firefox hides
  // it under resistFingerprinting and Safari says "Apple GPU" for every
  // device, both of which fall through to the pointer heuristic.
  const debugInfo = renderer.getContext().getExtension("WEBGL_debug_renderer_info");
  const rendererName = debugInfo
    ? (renderer.getContext().getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
    : null;
  const governor = createGovernor({
    startLevel: startLevelFor(coarsePointer, isSoftwareRenderer(rendererName)),
  });
  let lastFrameAt = 0;

  // Losing the context is routine on low-end Android -- backgrounding the tab
  // or GPU memory pressure is enough. Without these the canvas goes blank and
  // stays blank for the rest of the session, because the only guard was a
  // try/catch around construction. Preventing the default on loss is what
  // lets the browser hand back a restored context.
  let contextLost = false;
  const onLost = (e: Event) => {
    e.preventDefault();
    contextLost = true;
  };
  const onRestored = () => {
    contextLost = false;
    // Everything GL-side is gone; three rebuilds its own objects lazily, but
    // sizes and the measurement window have to be re-established.
    lastFrameAt = 0;
    sizeDirty = true;
    governor.reset();
  };
  canvas.addEventListener("webglcontextlost", onLost, false);
  canvas.addEventListener("webglcontextrestored", onRestored, false);

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(0, 1, 1, 0, 0.1, 2000);
  camera.position.set(0, 0, 100);

  const root = new THREE.Group();
  const inner = new THREE.Group();
  root.add(inner);
  scene.add(root);

  const overlay = new THREE.Scene();
  const overlayCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const white = makeFallback("#e8eef2", 8, 8);
  const snowImg = texFromImage(assets.snow, white);
  snowImg.wrapS = snowImg.wrapT = THREE.RepeatWrapping;
  // The baked noise the ground shader reads instead of evaluating fbm per
  // pixel; see tools/art/terrain.cjs. Both tile, both mip.
  const terrainImg = texFromImage(assets["snow-terrain"], white);
  terrainImg.wrapS = terrainImg.wrapT = THREE.RepeatWrapping;
  const noiseImg = texFromImage(assets["snow-noise"], white);
  noiseImg.wrapS = noiseImg.wrapT = THREE.RepeatWrapping;

  const snowUniforms = {
    uSnow: { value: snowImg },
    uTerrain: { value: terrainImg },
    uNoise: { value: noiseImg },
    // SLOPE in tools/art/terrain.cjs: the scale the baked height differences
    // were divided by to fit 0..1.
    uSlope: { value: 0.5 },
    // How much the snow tile's own height field perturbs the ground normal.
    uGrainBump: { value: 0.35 },
    uSunDir: { value: SUN_DIR },
    uTime: { value: 0 },
    uSpeed: { value: 0 },
    uAir: { value: 0 },
    uScale: { value: 1 },
    uCamX: { value: 0 },
    uCamY: { value: 0 },
    uViewW: { value: 1 },
    uViewH: { value: 1 },
    uSnowCenter: { value: new THREE.Vector2(0, 0) },
    uSnowSize: { value: new THREE.Vector2(1, 1) },
    uCourseW: { value: COURSE_WIDTH },
    uBank: { value: BANK },
    uReduced: { value: 0 },
    uSnowCol: { value: new THREE.Color("#eef3f6") },
    uPineCol: { value: new THREE.Color("#15241c") },
    uIceCol: { value: new THREE.Color("#c8d7e0") },
    uSunCol: { value: new THREE.Color("#ffe7c4") },
    uSkyCol: { value: new THREE.Color("#8aa3b5") },
  };
  const snowMat = new THREE.ShaderMaterial({
    uniforms: snowUniforms,
    vertexShader: SNOW_VERT,
    fragmentShader: SNOW_FRAG,
    // Half precision for the colour math. Mobile GPUs run fp16 at up to
    // twice the rate of fp32 and desktop ones ignore the hint, so this is
    // free where it does nothing and worth having where it counts. It is
    // safe only because the shader declares everything derived from the
    // world position highp: vWorld reaches 25600, where fp16 steps in
    // whole units, and the old sine hash would have collapsed entirely --
    // which is why this waited until the noise was baked out.
    precision: "mediump",
  });
  const snowMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), snowMat);
  snowMesh.frustumCulled = false;
  inner.add(snowMesh);

  const circleTex = makeCircleTex();
  const shadowGeo = new THREE.PlaneGeometry(1, 1);
  const shadowMat = new THREE.MeshBasicMaterial({
    map: circleTex,
    transparent: true,
    depthWrite: false,
  });
  const shadows = new THREE.InstancedMesh(shadowGeo, shadowMat, MAX_SHADOWS);
  shadows.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  shadows.frustumCulled = false;
  inner.add(shadows);

  const trackMat = new THREE.MeshBasicMaterial({
    map: circleTex,
    transparent: true,
    depthWrite: false,
    color: 0x6e8794,
    opacity: 0.38,
  });
  const tracks = new THREE.InstancedMesh(shadowGeo, trackMat, MAX_TRACKS);
  tracks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  tracks.frustumCulled = false;
  inner.add(tracks);

  const fallbacks: Record<string, THREE.Texture> = {
    "tree-pine": makeFallback("#24362c", 128, 128),
    "tree-hemlock": makeFallback("#1c2e24", 128, 128),
    "rock-0": makeFallback("#3d342c", 64, 64),
    jump: makeFallback("#c5cdc8", 96, 64),
    player: makeFallback("#c45c4a", 64, 64),
  };

  const fogUniforms = {
    uViewH: { value: 1 },
    uFogCol: { value: new THREE.Color("#121e19") },
    uSunCol: { value: new THREE.Color("#ffe7c4") },
    uFogAmt: { value: 1 },
  };

  const batches = new Map<string, THREE.InstancedMesh>();
  for (const key of SPRITE_KEYS) {
    const cap = MAX_PER[key];
    const tex = texFromImage(assets[key], fallbacks[key] ?? fallbacks["rock-0"]!);
    const img = assets[key];
    const texW = img && img.naturalWidth > 0 ? img.naturalWidth : 64;
    const texH = img && img.naturalHeight > 0 ? img.naturalHeight : 64;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: tex },
        uViewH: fogUniforms.uViewH,
        uFogCol: fogUniforms.uFogCol,
        uSunCol: fogUniforms.uSunCol,
        uFogAmt: fogUniforms.uFogAmt,
        uSunDir: { value: SUN_DIR },
        // One texel, for the rim light's step toward the sun.
        uTexel: { value: new THREE.Vector2(1 / texW, 1 / texH) },
      },
      vertexShader: SPRITE_VERT,
      fragmentShader: SPRITE_FRAG,
      // Scenery is an alpha cutout, not a blended surface: the fragment
      // shader discards below a threshold and every surviving pixel is fully
      // opaque. Marking it transparent bought nothing and cost two things --
      // painter's-order sorting on ~900 instances, and depth rejection across
      // roughly two screens of overdraw, because the transparent pass draws
      // back to front with depth writes doing no culling work.
      transparent: false,
      depthWrite: true,
      side: THREE.DoubleSide,
      // Turns the cutout's alpha into sample coverage when the target is
      // multisampled, which is the only way an alpha-tested edge gets
      // antialiased. Without MSAA it is a no-op.
      alphaToCoverage: true,
    });
    const mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, cap);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    for (let i = 0; i < cap; i++) mesh.setColorAt(i, WHITE);
    if (mesh.instanceColor) mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    inner.add(mesh);
    batches.set(key, mesh);
  }

  // A sprite is contained in the box it is given, centred, at its own aspect
  // -- not stretched to fill it. The masters are no longer all square: the
  // hemlock is a tall side elevation and the pine a near-square overhead
  // view, and they share one object definition, so the box in objects.ts is a
  // bound rather than a shape. Square art in a square box is unchanged.
  const aspect = new Map<string, number>();
  for (const key of SPRITE_KEYS) {
    const img = assets[key];
    aspect.set(key, img && img.naturalWidth > 0 ? img.naturalHeight / img.naturalWidth : 1);
  }
  function fit(key: string, boxW: number, boxH: number): [number, number] {
    const a = aspect.get(key) ?? 1;
    const w = Math.min(boxW, boxH / a);
    return [w, w * a];
  }

  const playerTex = [0, 1, 2, 3].map((i) => texFromImage(assets[`player-${i}`], fallbacks.player!));
  const playerMat = new THREE.MeshBasicMaterial({
    map: playerTex[0],
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const playerMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), playerMat);
  playerMesh.frustumCulled = false;
  inner.add(playerMesh);

  const glowMat = new THREE.MeshBasicMaterial({
    map: playerTex[0],
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    color: 0xc9d8e0,
    opacity: 0,
    side: THREE.DoubleSide,
  });
  const glowMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), glowMat);
  glowMesh.frustumCulled = false;
  inner.add(glowMesh);

  const plateMat = new THREE.MeshBasicMaterial({
    map: playerTex[0],
    transparent: true,
    depthWrite: false,
    color: 0x0c1612,
    opacity: 0.4,
    side: THREE.DoubleSide,
  });
  const plate = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), plateMat);
  plate.frustumCulled = false;
  inner.add(plate);

  const ghostMatA = new THREE.MeshBasicMaterial({
    map: playerTex[0],
    transparent: true,
    depthWrite: false,
    opacity: 0,
    side: THREE.DoubleSide,
  });
  const ghostMatB = ghostMatA.clone();
  const ghostA = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), ghostMatA);
  const ghostB = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), ghostMatB);
  ghostA.frustumCulled = ghostB.frustumCulled = false;
  inner.add(ghostA, ghostB);

  const playerShadowMat = new THREE.MeshBasicMaterial({
    map: circleTex,
    transparent: true,
    depthWrite: false,
    color: 0x0c1210,
    opacity: 0.32,
  });
  const playerShadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), playerShadowMat);
  playerShadow.frustumCulled = false;
  inner.add(playerShadow);

  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xf4f1ea,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.42, 0.5, 32), ringMat);
  ring.frustumCulled = false;
  inner.add(ring);

  const finishTex = makeFinishTex();
  const bannerMat = new THREE.MeshBasicMaterial({ map: finishTex, transparent: false });
  const banner = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), bannerMat);
  banner.frustumCulled = false;
  inner.add(banner);
  const poleGeo = new THREE.PlaneGeometry(1, 1);
  const poleMat = new THREE.MeshBasicMaterial({ color: 0x3d342c });
  const poleL = new THREE.Mesh(poleGeo, poleMat);
  const poleR = new THREE.Mesh(poleGeo, poleMat);
  poleL.frustumCulled = poleR.frustumCulled = false;
  inner.add(poleL, poleR);

  const sprayGeo = new THREE.BufferGeometry();
  const sprayPos = new Float32Array(MAX_SPRAY * 3);
  const sprayLife = new Float32Array(MAX_SPRAY);
  const spraySize = new Float32Array(MAX_SPRAY);
  sprayGeo.setAttribute(
    "position",
    new THREE.BufferAttribute(sprayPos, 3).setUsage(THREE.DynamicDrawUsage),
  );
  sprayGeo.setAttribute(
    "aLife",
    new THREE.BufferAttribute(sprayLife, 1).setUsage(THREE.DynamicDrawUsage),
  );
  sprayGeo.setAttribute(
    "aSize",
    new THREE.BufferAttribute(spraySize, 1).setUsage(THREE.DynamicDrawUsage),
  );
  sprayGeo.setDrawRange(0, 0);
  const sprayUniforms = {
    uScale: { value: 1 },
    uColor: { value: new THREE.Color("#f4f1ea") },
    uPixelScale: { value: 1 },
  };
  const sprayMat = new THREE.ShaderMaterial({
    uniforms: sprayUniforms,
    vertexShader: SPRAY_VERT,
    fragmentShader: SPRAY_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const spray = new THREE.Points(sprayGeo, sprayMat);
  spray.frustumCulled = false;
  inner.add(spray);

  const snowGeo = new THREE.BufferGeometry();
  const seeds = new Float32Array(SNOW_COUNT);
  for (let i = 0; i < SNOW_COUNT; i++) seeds[i] = Math.random() * 1000;
  snowGeo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
  snowGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(SNOW_COUNT * 3), 3));
  const snowFallUniforms = {
    uTime: { value: 0 },
    uSpeed: { value: 0 },
    uRes: { value: new THREE.Vector2(1, 1) },
    uReduced: { value: 0 },
    uPixelScale: { value: 1 },
  };
  const snowFallMat = new THREE.ShaderMaterial({
    uniforms: snowFallUniforms,
    vertexShader: SNOWFALL_VERT,
    fragmentShader: SNOWFALL_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });
  const snowFall = new THREE.Points(snowGeo, snowFallMat);
  snowFall.frustumCulled = false;
  overlay.add(snowFall);

  const compositeCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const compositeScene = new THREE.Scene();
  const compositeUniforms = {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
    uSpeed: { value: 0 },
    uAir: { value: 0 },
    uStun: { value: 0 },
    uReduced: { value: 0 },
    uRes: { value: new THREE.Vector2(1, 1) },
    uPlayer: { value: new THREE.Vector2(0.5, 0.72) },
    uPhaseTitle: { value: 1 },
    uUvScale: { value: new THREE.Vector2(1, 1) },
    uSunPos: { value: SUN_SCREEN.clone() },
    uHdr: { value: 0 },
    // The filmic curve darkens a value of 0.9 to 0.78; this brings the lane
    // back to where it sat while the highlights above 1 still roll off.
    uExposure: { value: 1.25 },
  };
  const compositeMat = new THREE.ShaderMaterial({
    uniforms: compositeUniforms,
    vertexShader: COMPOSITE_VERT,
    fragmentShader: COMPOSITE_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  compositeScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), compositeMat));

  // Rendering to a half-float target needs the colour-buffer-float
  // extension; filtering one is core in WebGL2.
  const gl = renderer.getContext();
  const canHdr =
    !!gl.getExtension("EXT_color_buffer_float") || !!gl.getExtension("EXT_color_buffer_half_float");

  // The world pass's target. Half-float gives the sun, the ice specular and
  // the sparkle headroom above 1 for the composite's filmic curve to roll
  // off instead of clipping flat; MSAA gives the sprite cutouts real edges
  // through alpha-to-coverage. Both cost bandwidth, which is the scarce
  // resource on a low-end phone, so both are tiered by the governor.
  function makeTarget(hdr: boolean, samples: number) {
    const target = new THREE.WebGLRenderTarget(4, 4, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      type: hdr ? THREE.HalfFloatType : THREE.UnsignedByteType,
      samples,
    });
    target.texture.colorSpace = THREE.NoColorSpace;
    return target;
  }
  let rt = makeTarget(false, 0);
  let rtHdr = false;
  let rtSamples = 0;

  const counts: Record<string, number> = {};

  function placeSprite(
    mesh: THREE.InstancedMesh,
    index: number,
    sx: number,
    sy: number,
    w: number,
    h: number,
    z: number,
    flip: boolean,
    viewH: number,
    rot = 0,
  ) {
    dummy.position.set(sx, viewH - sy, z);
    dummy.rotation.set(0, 0, -rot);
    dummy.scale.set(flip ? -w : w, h, 1);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
  }

  // Size is observed, not polled. Reading canvas.clientWidth inside render()
  // forced a synchronous layout flush every frame, and the HUD dirties layout
  // on alternating frames, so that read was never free. Worse, the canvas is
  // sized in dvh: on a phone the viewport height changes continuously while
  // the URL bar collapses, and an unguarded resize would reallocate the
  // drawing buffer and the render target at several ms apiece, a dozen times
  // inside one 200ms animation.
  let cssW = Math.max(1, canvas.clientWidth);
  let cssH = Math.max(1, canvas.clientHeight);
  let dpr = Math.min(2, window.devicePixelRatio || 1);
  let sizeDirty = true;

  function applySize() {
    sizeDirty = false;
    renderer.setPixelRatio(dpr);
    renderer.setSize(cssW, cssH, false);
    camera.left = 0;
    camera.right = cssW;
    camera.top = cssH;
    camera.bottom = 0;
    camera.updateProjectionMatrix();
    const pw = Math.max(2, Math.round(cssW * dpr));
    const ph = Math.max(2, Math.round(cssH * dpr));
    if (rt.width !== pw || rt.height !== ph) rt.setSize(pw, ph);
    snowFallUniforms.uRes.value.set(cssW, cssH);
    compositeUniforms.uRes.value.set(cssW, cssH);
    // The frames spanning a resize say nothing about steady-state cost.
    governor.reset();
  }

  const observer =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver((entries) => {
          for (const entry of entries) {
            const box = entry.contentBoxSize?.[0];
            const w = Math.max(1, Math.round(box ? box.inlineSize : entry.contentRect.width));
            const h = Math.max(1, Math.round(box ? box.blockSize : entry.contentRect.height));
            if (w !== cssW || h !== cssH) {
              cssW = w;
              cssH = h;
              sizeDirty = true;
            }
          }
        })
      : null;
  observer?.observe(canvas);

  // Zoom and moving a window between monitors change the ratio without
  // resizing the element, and reading it is a plain property access.
  function syncDpr() {
    const next = Math.min(2, window.devicePixelRatio || 1);
    if (next !== dpr) {
      dpr = next;
      sizeDirty = true;
    }
  }

  function updateBatches(sim: GameSim, cam: ViewCamera) {
    const visTop = cam.camY - 80;
    const visBot = cam.camY + cam.viewWorldH + 140;
    const viewH = cam.viewH;
    for (const key of SPRITE_KEYS) counts[key] = 0;
    let shadowN = 0;

    const addShadow = (sx: number, sy: number, w: number, h: number, z: number) => {
      if (shadowN >= MAX_SHADOWS) return;
      // Cast away from the sun, further for a taller object, instead of
      // sitting centred under it as a decal would.
      const reach = h * 0.16;
      dummy.position.set(
        sx + SHADOW_DIR.x * reach,
        viewH - sy - h * 0.12 + SHADOW_DIR.y * reach,
        Math.max(3, z - 0.4),
      );
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(w * 0.72, h * 0.28, 1);
      dummy.updateMatrix();
      shadows.setMatrixAt(shadowN++, dummy.matrix);
    };

    for (const s of sim.scenery) {
      if (s.y < visTop || s.y > visBot) continue;
      const key = s.sprite;
      const mesh = batches.get(key);
      if (!mesh) continue;
      const n = counts[key] ?? 0;
      if (n >= MAX_PER[key as keyof typeof MAX_PER]) continue;
      const box = 100 * s.scale * cam.scale;
      const [w, h] = fit(key, box, box);
      // Wind sway is for the trees; the bank boulders hold still.
      const sways = key.startsWith("tree") && !sim.reducedMotion;
      const sx = toScreenX(s.x, cam) + (sways ? Math.sin(sim.clock * 1.05 + s.y * 0.01) * 1.6 : 0);
      const sy = toScreenY(s.y, cam);
      const z = 10 + (sy / Math.max(viewH, 1)) * 30 + 6;
      placeSprite(mesh, n, sx, sy, w, h, z, s.flip, viewH);
      mesh.setColorAt(n, WHITE);
      counts[key] = n + 1;
      addShadow(sx, sy, w, h, z);
    }

    for (const e of sim.entities) {
      if (e.y < visTop || e.y > visBot) continue;
      const def = getObject(e.defId);
      // An eaten pickup is gone, not dimmed -- unlike a used ramp, which stays
      // on the hill as a landmark.
      if (e.consumed && def.collision === "pickup") continue;
      const key = def.sprites[e.variant % def.sprites.length]!;
      const mesh = batches.get(key);
      if (!mesh) continue;
      const n = counts[key] ?? 0;
      if (n >= (MAX_PER[key as keyof typeof MAX_PER] ?? 40)) continue;
      const fade = e.consumed && def.collision === "launch" ? 0.5 : 1;
      let [w, h] = fit(key, def.size.w * cam.scale, def.size.h * cam.scale);
      let lift = fade;
      if (def.collision === "pickup") {
        // Food breathes. At tuck speed the camera pulls back and the fog term
        // dims everything ahead; a slow size bob and a brightness pulse above
        // 1 -- which the HDR tier's bloom picks up as a soft shine -- keep the
        // reward legible without adding a single draw call.
        const pulse = sim.reducedMotion ? 0 : Math.sin(sim.clock * 3.4 + e.uid * 1.7);
        const grow = 1 + 0.05 * pulse;
        w *= grow;
        h *= grow;
        lift = 1.12 + 0.1 * pulse;
      }
      const sx = toScreenX(e.x, cam);
      const sy = toScreenY(e.y, cam);
      const z = 10 + (sy / Math.max(viewH, 1)) * 30 + def.zBias * 0.35;
      placeSprite(mesh, n, sx, sy, w, h, z, false, viewH);
      COLOR.setRGB(lift, lift, lift);
      mesh.setColorAt(n, COLOR);
      counts[key] = n + 1;
      addShadow(sx, sy, w * 0.85, h, z);
    }

    for (const key of SPRITE_KEYS) {
      const mesh = batches.get(key)!;
      mesh.count = counts[key] ?? 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    shadows.count = shadowN;
    shadows.instanceMatrix.needsUpdate = true;
  }

  function updateTracks(sim: GameSim, cam: ViewCamera) {
    const viewH = cam.viewH;
    const maxPairs = Math.floor(MAX_TRACKS / 2);
    const n = Math.min(sim.tracks.length, maxPairs);
    let k = 0;
    for (let i = 0; i < n; i++) {
      const t = sim.tracks[i]!;
      const sy = viewH - toScreenY(t.y, cam);
      const z = 5;
      const rw = 9 * cam.scale;
      const rh = 16 * cam.scale;
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(rw, rh, 1);
      dummy.position.set(toScreenX(t.x - 7, cam), sy, z);
      dummy.updateMatrix();
      tracks.setMatrixAt(k++, dummy.matrix);
      dummy.position.set(toScreenX(t.x + 7, cam), sy, z);
      dummy.updateMatrix();
      tracks.setMatrixAt(k++, dummy.matrix);
    }
    tracks.count = k;
    tracks.instanceMatrix.needsUpdate = true;
  }

  function updatePlayer(sim: GameSim, cam: ViewCamera) {
    const p = sim.player;
    const viewH = cam.viewH;
    const sx = toScreenX(p.x, cam);
    const sy = toScreenY(p.y, cam);
    const lift = p.height * 0.55 * cam.scale;
    const size = PLAYER_DRAW * (1 + p.height * 0.0045) * cam.scale;
    const z = 40;
    const ty = viewH - sy + lift;
    const frame = Math.floor(p.frame) % 4;
    const map = playerTex[frame] ?? playerTex[0]!;
    playerMat.map = map;
    playerMat.needsUpdate = true;
    playerMat.opacity = p.stun > 0 && Math.floor(sim.clock * 18) % 2 === 0 ? 0.55 : 1;
    playerMesh.position.set(sx, ty, z + 1);
    playerMesh.rotation.z = -p.lean * 0.32;
    playerMesh.scale.set(size * p.squash, size * (2 - p.squash), 1);

    plateMat.map = map;
    plate.position.set(sx, ty - 1.2 * cam.scale, z + 0.2);
    plate.rotation.z = -p.lean * 0.32;
    plate.scale.set(size * 1.12 * p.squash, size * 1.14 * (2 - p.squash), 1);
    plateMat.opacity = 0.22;

    const spd = speed01(p.speedY);
    const glow = Math.max(spd - 0.35, 0) * 0.6 + (p.height > AIR_PASS_HEIGHT ? 0.32 : 0);
    glowMat.map = map;
    glowMat.opacity = sim.reducedMotion ? 0 : glow;
    glowMesh.position.set(sx, ty, z + 0.5);
    glowMesh.rotation.z = -p.lean * 0.32;
    glowMesh.scale.set(size * 1.32 * p.squash, size * 1.32 * (2 - p.squash), 1);

    const ghostOn = !sim.reducedMotion && spd > 0.28 && p.stun <= 0 && sim.phase === "playing";
    const gA = ghostOn ? (spd - 0.28) * 0.28 : 0;
    ghostMatA.map = map;
    ghostMatB.map = map;
    ghostMatA.opacity = gA;
    ghostMatB.opacity = gA * 0.45;
    const trail = 10 * cam.scale * spd;
    ghostA.position.set(sx - p.lean * 4, ty + trail, z - 0.2);
    ghostB.position.set(sx - p.lean * 8, ty + trail * 1.85, z - 0.35);
    ghostA.rotation.z = ghostB.rotation.z = -p.lean * 0.32;
    ghostA.scale.set(size * p.squash, size * (2 - p.squash), 1);
    ghostB.scale.set(size * 0.92 * p.squash, size * 0.92 * (2 - p.squash), 1);

    const airScale = 1 + p.height * 0.012;
    // The shadow stays on the snow and slides away from the sun as the rider
    // rises, which is what says "airborne" more than the sprite growing does.
    const drop = lift * 0.6;
    playerShadow.position.set(
      sx + SHADOW_DIR.x * drop,
      viewH - sy + 10 * cam.scale + SHADOW_DIR.y * drop,
      8,
    );
    playerShadow.scale.set((size * 0.55) / airScale, (size * 0.2) / (1 + p.height * 0.014), 1);
    playerShadowMat.opacity = 0.3 * (0.4 + Math.min(0.5, p.height / 180));

    ringMat.opacity = p.height > AIR_PASS_HEIGHT ? 0.38 : 0;
    ring.position.set(sx, ty, z + 1.2);
    ring.scale.set(size, size * 0.42, 1);
  }

  function updateFinish(cam: ViewCamera) {
    const viewH = cam.viewH;
    const a = toScreenX(18, cam);
    const b = toScreenX(COURSE_WIDTH - 18, cam);
    const sy = toScreenY(TRACK_LENGTH, cam);
    const bannerH = 26 * cam.scale;
    const ty = viewH - sy;
    const z = 20;
    banner.position.set((a + b) / 2, ty + 40 * cam.scale - bannerH / 2, z);
    banner.scale.set(Math.max(1, b - a), bannerH, 1);
    poleL.position.set(a, ty + 13 * cam.scale, z);
    poleR.position.set(b, ty + 13 * cam.scale, z);
    poleL.scale.set(6 * cam.scale, 46 * cam.scale, 1);
    poleR.scale.set(6 * cam.scale, 46 * cam.scale, 1);
  }

  function updateSpray(sim: GameSim, cam: ViewCamera) {
    const n = Math.min(sim.particles.length, MAX_SPRAY);
    const viewH = cam.viewH;
    for (let i = 0; i < n; i++) {
      const q = sim.particles[i]!;
      sprayPos[i * 3] = toScreenX(q.x, cam);
      sprayPos[i * 3 + 1] = viewH - toScreenY(q.y, cam);
      sprayPos[i * 3 + 2] = 36;
      sprayLife[i] = Math.max(0, q.max > 0 ? q.life / q.max : 0);
      spraySize[i] = q.size * (q.kind === "burst" ? 1.55 : 1.1);
    }
    sprayGeo.setDrawRange(0, n);
    (sprayGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (sprayGeo.attributes.aLife as THREE.BufferAttribute).needsUpdate = true;
    (sprayGeo.attributes.aSize as THREE.BufferAttribute).needsUpdate = true;
    sprayUniforms.uScale.value = cam.scale;
  }

  function render(sim: GameSim, _alpha: number) {
    void _alpha;
    if (contextLost) return;

    const now = performance.now();
    if (lastFrameAt > 0) governor.sample(now - lastFrameAt);
    lastFrameAt = now;

    syncDpr();
    if (sizeDirty) applySize();
    const viewW = cssW;
    const viewH = cssH;

    // The two most expensive things a frame can buy, only on the levels
    // that have earned them. Recreating the target is a reallocation, but
    // the governor's cooldown and latch already keep level changes rare.
    const level = isFullResolution() ? TOP_LEVEL : governor.level();
    const wantHdr = canHdr && level >= 2;
    const wantSamples = level >= 3 ? 4 : 0;
    if (wantHdr !== rtHdr || wantSamples !== rtSamples) {
      rt.dispose();
      rt = makeTarget(wantHdr, wantSamples);
      rtHdr = wantHdr;
      rtSamples = wantSamples;
      rt.setSize(Math.max(2, Math.round(cssW * dpr)), Math.max(2, Math.round(cssH * dpr)));
      compositeUniforms.uHdr.value = wantHdr ? 1 : 0;
    }
    const cam = computeView(sim, viewW, viewH);

    const pivotY = viewH * (1 - cam.frac);
    root.position.set(viewW / 2 + cam.ox, pivotY - cam.oy, 0);
    root.rotation.z = -sim.camRoll;
    inner.position.set(-viewW / 2, -pivotY, 0);

    const air = sim.player.height > AIR_PASS_HEIGHT ? 1 : 0;
    const pad = 80;
    // The ground quad must reach the viewport edges, not stop at the bank
    // band: the fragment shader derives world x per pixel and paints the deep
    // woods out there, and a height-fit desktop viewport sees well past the
    // banks -- a band-sized quad clips the forest into a hard seam against
    // the clear colour. On a phone the band already overflows the viewport,
    // so the min/max changes nothing. The 60 covers the camera roll.
    const x0 = Math.min(toScreenX(-BANK, cam), -60);
    const x1 = Math.max(toScreenX(COURSE_WIDTH + BANK, cam), viewW + 60);
    const y0 = viewH - toScreenY(cam.camY - pad, cam);
    const y1 = viewH - toScreenY(cam.camY + cam.viewWorldH + pad, cam);
    snowMesh.position.set((x0 + x1) / 2, (y0 + y1) / 2, 2);
    snowMesh.scale.set(Math.max(1, x1 - x0), Math.max(1, Math.abs(y0 - y1)), 1);
    snowUniforms.uTime.value = sim.clock;
    snowUniforms.uSpeed.value = sim.player.speedY;
    snowUniforms.uAir.value = air;
    snowUniforms.uScale.value = cam.scale;
    snowUniforms.uCamX.value = cam.camX;
    snowUniforms.uCamY.value = cam.camY;
    snowUniforms.uViewW.value = viewW;
    snowUniforms.uViewH.value = viewH;
    snowUniforms.uReduced.value = sim.reducedMotion ? 1 : 0;
    snowUniforms.uSnowCenter.value.set(snowMesh.position.x, snowMesh.position.y);
    snowUniforms.uSnowSize.value.set(snowMesh.scale.x, snowMesh.scale.y);
    fogUniforms.uViewH.value = viewH;
    fogUniforms.uFogAmt.value = sim.reducedMotion ? 0.45 : 1;

    updateBatches(sim, cam);
    updateTracks(sim, cam);
    updatePlayer(sim, cam);
    updateFinish(cam);
    updateSpray(sim, cam);

    snowGeo.setDrawRange(0, sim.reducedMotion ? 220 : viewW < 500 ? 980 : SNOW_COUNT);
    snowFallUniforms.uTime.value = sim.clock;
    snowFallUniforms.uSpeed.value = sim.player.speedY;
    snowFallUniforms.uReduced.value = sim.reducedMotion ? 1 : 0;

    compositeUniforms.uTime.value = sim.clock;
    compositeUniforms.uSpeed.value = Math.max(
      0,
      (sim.player.speedY - CRUISE_SPEED) / (MAX_SPEED - CRUISE_SPEED),
    );
    compositeUniforms.uAir.value = air;
    compositeUniforms.uStun.value = Math.max(0, Math.min(1, sim.player.stun * 3));
    compositeUniforms.uReduced.value = sim.reducedMotion ? 1 : 0;
    compositeUniforms.uPlayer.value.set(0.5, 1 - cam.frac);
    compositeUniforms.uPhaseTitle.value = sim.phase === "title" ? 1 : 0;

    // The world pass draws into a sub-rect of a full-size target and the
    // composite stretches it back out. Scaling this way rather than through
    // setPixelRatio matters: assigning canvas.width reallocates the drawing
    // buffer, which costs milliseconds, so driving resolution off the canvas
    // would hitch on every step. A viewport change costs nothing, which is
    // what makes per-frame adjustment safe.
    const scale = isFullResolution() ? 1 : scaleForBudget(governor.budget(), rt.width, rt.height);
    const vpW = viewW * scale;
    const vpH = viewH * scale;
    // three floors the viewport after multiplying by the pixel ratio, so
    // mirror that here or the composite samples half a pixel of stale target.
    const subW = Math.min(rt.width, Math.max(2, Math.floor(vpW * dpr)));
    const subH = Math.min(rt.height, Math.max(2, Math.floor(vpH * dpr)));
    compositeUniforms.uUvScale.value.set((subW - 0.5) / rt.width, (subH - 0.5) / rt.height);
    snowFallUniforms.uPixelScale.value = scale;
    sprayUniforms.uPixelScale.value = scale;

    renderer.setRenderTarget(rt);
    renderer.setViewport(0, 0, vpW, vpH);
    // Scissored so the clear only touches the pixels the world pass will
    // actually write, instead of the whole target every frame.
    renderer.setScissor(0, 0, vpW, vpH);
    renderer.setScissorTest(true);
    renderer.clear();
    renderer.render(scene, camera);
    renderer.autoClear = false;
    renderer.render(overlay, overlayCam);
    renderer.autoClear = true;
    renderer.setScissorTest(false);
    renderer.setRenderTarget(null);
    renderer.setViewport(0, 0, viewW, viewH);
    compositeUniforms.tDiffuse.value = rt.texture;
    renderer.render(compositeScene, compositeCam);
  }

  function dispose() {
    observer?.disconnect();
    canvas.removeEventListener("webglcontextlost", onLost);
    canvas.removeEventListener("webglcontextrestored", onRestored);
    rt.dispose();
    renderer.dispose();
    snowMesh.geometry.dispose();
    snowMat.dispose();
    shadowGeo.dispose();
    shadowMat.dispose();
    circleTex.dispose();
    if (snowImg !== white) snowImg.dispose();
    if (terrainImg !== white) terrainImg.dispose();
    if (noiseImg !== white) noiseImg.dispose();
    white.dispose();
    finishTex.dispose();
    banner.geometry.dispose();
    bannerMat.dispose();
    poleGeo.dispose();
    poleMat.dispose();
    playerMesh.geometry.dispose();
    playerMat.dispose();
    glowMesh.geometry.dispose();
    glowMat.dispose();
    plate.geometry.dispose();
    plateMat.dispose();
    ghostA.geometry.dispose();
    ghostB.geometry.dispose();
    ghostMatA.dispose();
    ghostMatB.dispose();
    playerShadow.geometry.dispose();
    playerShadowMat.dispose();
    ring.geometry.dispose();
    ringMat.dispose();
    sprayGeo.dispose();
    sprayMat.dispose();
    snowGeo.dispose();
    snowFallMat.dispose();
    compositeMat.dispose();
    for (const mesh of batches.values()) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    for (const t of playerTex) if (t !== fallbacks.player) t.dispose();
    for (const t of Object.values(fallbacks)) t.dispose();
  }

  return { render, dispose };
}
